import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, UserX, Trash, RefreshCcw } from 'lucide-react';

export const FacultyTable = ({ data, onDeactivate, onDelete, onReactivate, viewPathPrefix = '/faculty/members', loading }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="animate-pulse">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center p-4 border-b border-slate-100 dark:border-slate-800">
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/6"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-md border border-slate-200 dark:border-slate-700 p-12 text-center">
        <p className="text-slate-500 dark:text-slate-400">No faculty records found</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="faculty-table">
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
            <th className="text-left font-medium text-slate-500 dark:text-slate-400 p-4">Name</th>
            <th className="text-left font-medium text-slate-500 dark:text-slate-400 p-4">Phone</th>
            <th className="text-left font-medium text-slate-500 dark:text-slate-400 p-4">Subjects</th>
            <th className="text-left font-medium text-slate-500 dark:text-slate-400 p-4">Status</th>
            <th className="text-right font-medium text-slate-500 dark:text-slate-400 p-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map((faculty) => (
            <tr 
              key={faculty.id} 
              className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors"
              data-testid={`faculty-row-${faculty.id}`}
            >
              <td className="p-4">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{faculty.name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{faculty.email}</p>
                </div>
              </td>
              <td className="p-4 text-sm text-slate-700 dark:text-slate-300">{faculty.phone}</td>
              <td className="p-4">
                <div className="flex flex-wrap gap-1">
                  {faculty.subjects.slice(0, 2).map((subject, idx) => (
                    <span 
                      key={idx}
                      className="bg-primary/10 text-primary dark:bg-accent-cool/20 dark:text-accent-cool px-2 py-0.5 rounded text-xs"
                    >
                      {subject}
                    </span>
                  ))}
                  {faculty.subjects.length > 2 && (
                    <span className="text-xs text-slate-500">+{faculty.subjects.length - 2}</span>
                  )}
                </div>
              </td>
              <td className="p-4">
                <span 
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    faculty.status === 'active'
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                      : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700'
                  }`}
                  data-testid={`faculty-status-${faculty.id}`}
                >
                  {faculty.status}
                </span>
              </td>
              <td className="p-4">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => navigate(`${viewPathPrefix}/${faculty.id}`)}
                    className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors text-slate-600 dark:text-slate-400"
                    data-testid={`view-faculty-${faculty.id}`}
                    title="View profile"
                  >
                    <Eye size={18} />
                  </button>
                  {faculty.status === 'active' && onDeactivate && (
                    <button
                      onClick={() => onDeactivate(faculty.id)}
                      className="p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-500"
                      data-testid={`deactivate-faculty-${faculty.id}`}
                      title="Deactivate"
                    >
                      <UserX size={18} />
                    </button>
                  )}
                  {faculty.status === 'active' && onDelete && (
                    <button
                      onClick={() => onDelete(faculty.id)}
                      className="p-2 rounded-md hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors text-red-600"
                      data-testid={`delete-faculty-${faculty.id}`}
                      title="Delete"
                    >
                      <Trash size={18} />
                    </button>
                  )}
                  {faculty.status !== 'active' && onReactivate && (
                    <button
                      onClick={() => onReactivate(faculty.id)}
                      className="p-2 rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-900/20 transition-colors text-emerald-600"
                      data-testid={`reactivate-faculty-${faculty.id}`}
                      title="Reactivate"
                    >
                      <RefreshCcw size={18} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
